import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { PageTitle, Panel, btnPrimary } from "./ui";

const Row = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1 border-b border-border py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={testId}>
    <span className="text-sm text-muted-foreground">{label}</span><span className="text-sm font-semibold">{children}</span>
  </div>
);
const Badge = ({ ok, children }) => <span className={`rounded px-2 py-0.5 text-xs font-bold ${ok ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-800"}`}>{children}</span>;

export default function AnalyticsIntegrations({ embedded = false }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/analytics/integration").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []);
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/analytics/check-tag");
      data.ok ? toast.success(data.message) : toast.error(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!d) return <p>Učitavanje…</p>;
  const tc = d.tag_check;
  return (
    <div data-testid="analytics-integrations-page">
      {!embedded && <PageTitle title="Analitika – integracije" sub="Stanje mjerenja posjeta. Statistika iz GA4 se ovdje ne prikazuje dok izvještaji nisu povezani." />}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Google Analytics 4" testId="ga4-panel">
          <Row label="Provider" testId="ga4-provider">{d.provider}</Row>
          <Row label="Measurement ID" testId="ga4-measurement-id"><code>{d.measurement_id}</code></Row>
          <Row label="Instalacija taga" testId="ga4-tag-status"><Badge ok>gtag.js ugrađen u javni dio (samo nakon saglasnosti)</Badge></Row>
          <Row label="Provjera taga kod Googlea" testId="ga4-tag-check">{tc ? <><Badge ok={tc.ok}>{tc.ok ? "Uspješno" : "Neuspješno"}</Badge> <span className="ml-2 font-normal">{fmtDateTime(tc.checked_at)}</span></> : <Badge>Još nije provjereno</Badge>}</Row>
          <Row label="Posljednje učitavanje taga kod posjetioca" testId="ga4-last-load">{d.last_tag_load ? fmtDateTime(d.last_tag_load) : "Još nije zabilježeno"}</Row>
          <Row label="Saglasnost (Consent Mode v2)" testId="ga4-consent-status"><Badge ok>Basic mode · sva 4 signala zadano „denied“</Badge></Row>
          <Row label="Izvještaji (Data API)" testId="ga4-reporting-status"><Badge ok={d.reporting.connected}>{d.reporting.status}</Badge></Row>
          <button onClick={check} disabled={busy} className={`${btnPrimary} mt-4`} data-testid="ga4-check-tag-button">{busy ? "Provjeravam…" : "Provjeri tag sada"}</button>
        </Panel>
        <Panel title="Napomene" testId="ga4-notes">
          <ul className="list-disc space-y-2 pl-5 text-sm">
            <li>„Posljednje učitavanje taga“ bilježi da se gtag.js učitao u pregledniku posjetioca koji je dao saglasnost. To ne potvrđuje da je Google obradio podatke – to se provjerava u GA4 DebugView / Realtime.</li>
            <li>Ugrađeni tag ne daje administraciji pristup GA4 izvještajima. Za izvještaje su potrebni GA4 Property ID i servisni račun s ulogom Viewer.</li>
            <li>U GA4 → Data streams → Enhanced measurement isključite „Page changes based on browser history events“, jer portal sam šalje page_view pri svakoj promjeni stranice.</li>
            <li>Administracija (/admin) se ne mjeri.</li>
          </ul>
          <p className="mt-4 text-sm">Newsletter: potvrđeno <b data-testid="nl-confirmed">{d.newsletter.confirmed}</b> · čeka potvrdu <b data-testid="nl-pending">{d.newsletter.pending}</b></p>
        </Panel>
        <Panel title="Google Search Console" testId="gsc-panel">
          <Row label="Status" testId="gsc-status-row"><Badge>{d.search_console.status}</Badge></Row>
          <Row label="Zadnja sinhronizacija">{d.search_console.last_sync ? fmtDateTime(d.search_console.last_sync) : "Nikad"}</Row>
          <p className="mt-3 text-xs text-muted-foreground">{d.search_console.setup}</p>
          <p className="mt-2 text-xs text-muted-foreground">GA4 izvještaji: {d.ga4_reporting_setup}</p>
        </Panel>
        <Panel title="Ostale integracije" testId="other-integrations">
          <Row label="BigQuery (opcionalno)"><Badge>{d.bigquery.status}</Badge></Row>
          {d.social.map((s) => <Row key={s.platform} label={s.platform}><Badge>{s.status}</Badge></Row>)}
          <p className="mt-3 text-xs font-semibold">Mapiranje metrika (GA4 i vlastito mjerenje se nikad ne sabiraju)</p>
          <ul className="mt-1 text-xs">{d.metric_mapping.map((m) => <li key={m.local}>{m.local} ↔ <code>{m.ga4}</code></li>)}</ul>
          <p className="mt-2 text-xs text-muted-foreground">Greške sinhronizacije: {d.sync_errors.length ? d.sync_errors.length : "nema (nema povezanih izvještaja)"}</p>
        </Panel>
      </div>
    </div>
  );
}
